<script setup lang="ts">
import type {
  LineRichMenuInput,
  LineRichMenuTemplateAction,
} from '@/types/schema.generated'
import {
  LineRichMenuStatus,
  LineRichMenuActionType,
  TargetType,
} from '@/types/schema.generated'
import {
  type LineRichMenuEditPageContentLineRichMenuPayloadFragment,
  useLineRichMenuEditPageContentQuery,
  useUpdateLineRichMenuMutation,
} from './LineRichMenuEditPageContent.generated'
import { useAlert } from '@/libs/alertPortal'
import { ref, computed } from 'vue'
import { nanoid } from 'nanoid'
import {
  LineRichMenuFormActionTypeLink,
  LineRichMenuFormActionTypeText,
  LineRichMenuFormStatusPublished,
  LineRichMenuFormStatusUnpublished,
  type LineRichMenuFormBackgroundImage,
  type LineRichMenuFormTemplateActionType,
  type LineRichMenuFormTemplateName,
  type LineRichMenuFormType,
} from '@/components/organisms/LineRichMenu/formSchema'
import LineRichMenuForm from '@/components/organisms/LineRichMenu/LineRichMenuForm.vue'
import { getAPIBaseURL } from '@/config'

const props = defineProps<{
  payload: LineRichMenuEditPageContentLineRichMenuPayloadFragment
}>()

const alert = useAlert()
const isSubmitting = ref(false)

const { result: queryResult } = useLineRichMenuEditPageContentQuery()

const initialForm = computed<LineRichMenuFormType>(() => {
  const status =
    props.payload.status === LineRichMenuStatus.Published
      ? LineRichMenuFormStatusPublished
      : props.payload.status === LineRichMenuStatus.Unpublished
        ? LineRichMenuFormStatusUnpublished
        : undefined
  if (!status) {
    throw new Error('invalid status')
  }

  const templateActions: LineRichMenuFormTemplateActionType[] = props.payload
    .templateActions
    ? props.payload.templateActions.map(
        (templateAction: LineRichMenuTemplateAction) => {
          const actionType =
            templateAction.actionType === LineRichMenuActionType.Link
              ? LineRichMenuFormActionTypeLink
              : templateAction.actionType === LineRichMenuActionType.Text
                ? LineRichMenuFormActionTypeText
                : undefined
          if (!actionType) {
            throw new Error('invalid action type')
          }

          return {
            key: nanoid(),
            panelName: templateAction.panelName,
            actionType: actionType,
            url: templateAction.url || '',
            label: templateAction.label || '',
            text: templateAction.text || '',
          }
        }
      )
    : []

  const imgixDomain = new URL(getAPIBaseURL('imgix')).host
  const backgroundImage: LineRichMenuFormBackgroundImage | null =
    props.payload.backgroundImageFileName !== ''
      ? {
          name: props.payload.backgroundImageFileName,
          image: `https://${imgixDomain}/line-rich-menu/${props.payload.backgroundImageFileName}`,
          size: props.payload.templateName.includes('large')
            ? 'large'
            : 'small',
        }
      : null

  const segment = (() => {
    if (props.payload.targetType !== TargetType.Segment) {
      return undefined
    }
    const conditions = props.payload?.segment?.conditions
    if (conditions == undefined) {
      return undefined
    }
    const segmentConditions = conditions
      .map(condition => {
        switch (condition.__typename) {
          case 'SegmentConditionLoginLink':
            return {
              type: 'line-activation-login-link',
              not: condition.not,
            }
            break
          case 'SegmentConditionInflowSource':
            return {
              type: 'inflow-source',
              not: condition.not,
              inflowSourceDefinitionGUID: condition.inflowSourceDefinitionGUID,
            }
            break
          case 'SegmentConditionClickURLCondition':
            return {
              type: 'click-url',
              not: condition.not,
              url: condition.url,
            }
            break
          case 'SegmentConditionEnqueteAnswered':
            return {
              type: 'planner-enquete-answered',
              plannerEnqueteGUID: condition.enqueteGUID,
              not: condition.not,
            }
            break
          case 'SegmentConditionRoomStatus':
            return {
              type: 'room-status',
              status: condition.status,
              not: condition.not,
            }
            break
          default:
            return undefined
        }
      })
      .filter(condition => condition !== undefined)
    return {
      type: 'segment',
      segment: {
        segmentConditions: segmentConditions,
      },
    }
  })()

  return {
    label: props.payload.label,
    status: status,
    templateName: props.payload.templateName as LineRichMenuFormTemplateName,
    backgroundImage: backgroundImage,
    templateActions: templateActions,
    chatBarText: props.payload.chatBarText,
    isDisplayDefault: props.payload.isDisplayDefault,
    targetType: props.payload.targetType,
    segment: segment,
  } as LineRichMenuFormType
})

const updateMutation = useUpdateLineRichMenuMutation()

const handleSubmit = async (input: LineRichMenuInput) => {
  const res = await updateMutation.mutate({
    input: {
      guid: props.payload.guid,
      lineRichMenu: input,
    },
  })
  if (!res) {
    isSubmitting.value = false
    alert.error('リッチメニューの更新に失敗しました')
    return
  }

  if (res.errors) {
    isSubmitting.value = false
    console.error(res.errors)
    alert.error('リッチメニューの更新に失敗しました')
    return
  }

  const data = res.data
  if (!data) {
    isSubmitting.value = false
    alert.error('リッチメニューの更新に失敗しました')
    return
  }

  if (
    data.updateLineRichMenu.__typename !== 'UpdateLineRichMenuSuccessPayload'
  ) {
    if (
      data.updateLineRichMenu.__typename === 'LineRichMenuErrorPayload' &&
      data.updateLineRichMenu.reason === 'DUPLICATE_SEGMENT_PUBLISHED'
    ) {
      isSubmitting.value = false
      alert.error(
        '同じセグメントのリッチメニューは1つしか公開できません。公開するにはすでに公開されている同じセグメントのリッチメニューを非公開にしてください。'
      )
      return
    }

    isSubmitting.value = false
    alert.error('リッチメニューの更新に失敗しました')
    return
  }

  isSubmitting.value = false
  alert.success('リッチメニューを更新しました')
}
</script>

<template>
  <div v-if="queryResult">
    <LineRichMenuForm
      v-model:isSubmitting="isSubmitting"
      :initial-form="initialForm"
      :userRegistrationInflowSourceDefinitions="
        queryResult.segmentConditionParameters.inflowSourceCondition
          .inflowSources
      "
      :clickURLs="
        queryResult.segmentConditionParameters.clickURLCondition.clickUrls
      "
      :plannerEnquetes="
        queryResult.segmentConditionParameters.enqueteAnsweredCondition.enquetes
      "
      @submit="handleSubmit"
    />
  </div>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 860px;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}
</style>
